
import server from '../../../integration/server/server-services';
import security from '../../../service/security/security-services';
import {IWindowService} from "angular";
import {UserSession} from "../contract-features-3/domain";
import {VersionInfo} from "../../../integration/representation/version-info";
import {User} from "../../../integration/representation/user";

interface DisplayUser extends User {
    forvalter: boolean;
    admin: boolean;
}

interface AdminControllerState {
    vm: { test(value: string): boolean; };
    currentUser: UserSession;
    users: DisplayUser[];
    filtered: User[];
    newUser: string;
    newUserRoleSelect: string;
    userInclude: string;
    usernameFilter: string;
    versionInfo: VersionInfo;

    toggleUserRole: Function;
    includeFlagChanged: Function;
    addLdapUser: Function;
    isCurrentUser: Function;
    deleteUser: Function;
    usernamesFiltered: Function;
    isFeatureToggleEnabled: Function;
    toggleFeature: Function;
    reload: Function;
    newUserValidation: { test(value: string): boolean };
}

export default function AdminController(server: server, security: security, $window: IWindowService){
    const vm: AdminControllerState = this;
    vm.toggleUserRole = toggleUserRole;
    vm.includeFlagChanged = includeFlagChanged;
    vm.addLdapUser = addLdapUser;
    vm.isCurrentUser = isCurrentUser;
    vm.deleteUser = deleteUser;
    vm.usernamesFiltered = usernamesFiltered;
    vm.isFeatureToggleEnabled = isFeatureToggleEnabled;
    vm.toggleFeature = toggleFeature;
    vm.reload = reload.bind($window);

    vm.newUser = '';
    vm.newUserRoleSelect = 'FORVALTER';
    vm.userInclude = 'all';
    vm.usernameFilter = '';
    vm.newUserValidation = (function newUserValidation() {
        const vvInternalUsersRegex = /^[a-zA-Z0-9]{6}$/;
        const vvExternalUsersRegex = /^[a-zA-Z0-9]{8}$/;
        return {
            test(value: string) {
                return vvInternalUsersRegex.test(value) || vvExternalUsersRegex.test(value);
            }
        };
    })();

    function isFeatureToggleEnabled(feature: string){
        return vm.currentUser && vm.currentUser.featureToggles.includes(feature);
    }

    function toggleFeature(feature: string) {
        server.toggleFeature(feature).then( function(){
            vm.reload();
        });
    }

    function reload() {
      this.location.reload();
    }


    security.ensureCurrentUser().then(function(user){
        vm.currentUser = user;

        if (vm.currentUser.hasPermission("USER:BROWSE")) {
            server.getAllUsers(vm.userInclude, vm.usernameFilter).then(function (users) {
                vm.users = users.map(u => mapUserRoles(u.roles, u));
            });
        }
    });

    server.getVersionInfo().then(function(info) {
        vm.versionInfo = info;
    });

    function usernamesFiltered() {
        vm.filtered = !!vm.users ? vm.users.filter(user => usernameMatch(user.username, vm.usernameFilter)) : [];
        return vm.filtered;
    }

    function usernameMatch(username: string, search: string) {
        return !search || searchMatches(username, search);
    }

    function searchMatches(property: any, searchWord: string) {
        return property && property.toString().toLowerCase().indexOf(searchWord) != -1;
    }

    function deleteUser(user: DisplayUser) {
        server.deleteUser(user.username);
        const idx = vm.users.indexOf(user);
        vm.users.splice(idx, 1);
    }

    function addLdapUser() {
        const username = vm.newUser.toLowerCase();
        if (isCurrentUser(username)) {
            vm.newUser = '';
            return;
        }

        let u = getUser(username);
        let isNewUser = false;
        if (u == null) {
            u = {
                username,
                name: undefined,
                emailAddress: undefined,
                roles: [],
                status: undefined,
                forvalter: false,
                admin: false
            };
            isNewUser = true;
        }

        toggleUserRole(u, vm.newUserRoleSelect);
        if (isNewUser) {
            vm.users.unshift(u);
        }
        vm.newUser = '';
    }

    function isCurrentUser(username: string) {
        return vm.currentUser && vm.currentUser.username === username;
    }

    function includeFlagChanged(newValue: string) {
        vm.userInclude = newValue;
        server.getAllUsers(newValue, vm.usernameFilter).then(function(users) {
            vm.users = users.map(u => mapUserRoles(u.roles, u));
        });
    }

    function toggleUserRole(user: DisplayUser, role: string) {
        const existingRoles = rolesForUser(user);

        const idx = existingRoles.indexOf(role);
        let roles: string[];
        if (idx === -1) {
            roles = [role].concat(existingRoles);
        } else {
            roles = existingRoles.filter(function(r){
                return r !== role;
            });
        }

        server.setRolesForUser(roles, user.username).then(() => {
            let updated = mapUserRoles(roles, user);
            user.admin = updated.admin;
            user.forvalter = updated.forvalter;
        })
    }

    function mapUserRoles(roles: string[], user: User): DisplayUser {
        const admin = roles.indexOf('ADMIN') !== -1;
        const forvalter = roles.indexOf('FORVALTER') !== -1;
        return {...user, admin, forvalter};
    }

    function getUser(username: string): DisplayUser {
        const users = vm.users.filter(function(u){
            return u.username === username;
        });

        return users.length > 0 ? users[0] : null;
    }

    function rolesForUser(user: DisplayUser): string[] {
        let existingRoles: string[] = [];
        if (user.admin) {
            existingRoles = existingRoles.concat('ADMIN');
        }
        if (user.forvalter) {
            existingRoles = existingRoles.concat('FORVALTER');
        }

        return existingRoles;
    }
}
AdminController.$inject = ['server', 'security', '$window'];
